'use client';

import type { CSSProperties } from 'react';

/** One sibling venue in the same hospitality group. */
export interface GroupMemberOption {
  /** The venue's UUID. */
  venueId: string;
  /** Display name (e.g. "Ayayay - Mexican Eatery"). */
  name: string;
  /** The venue's public slug — builds the /book/<slug> link. Null = no hosted page → not linkable. */
  slug?: string | null;
}

/**
 * The slice of the widget theme the links row needs. Kept as a standalone
 * interface (not a re-import of CateringWidgetTheme) so this component stays
 * decoupled and reusable across any multi-venue widget.
 */
export interface GroupSwitcherTheme {
  textPrimary: string;
  textSecondary: string;
  borderColor: string;
  surfacePanel: string;
  primaryColor: string;
  fontBody: string;
}

/**
 * Subtle "Interested in our other locations?" row for venues that belong to a
 * hospitality group. Each sibling renders as a plain hyperlink to its OWN
 * hosted booking page (/book/<slug>), where the URL, branding, agent, and
 * booking attribution are all that venue's.
 *
 * This deliberately REPLACES the earlier in-place <select> switcher, which
 * swapped the connected agent + booking attribution to a sibling venue while
 * the URL and page chrome still showed the original venue (customers could
 * book Ayayay under /book/akiro with Akiro branding). Navigation keeps every
 * surface self-consistent. Renders nothing when there are no linkable
 * siblings, so standalone venues are visually unaffected.
 */
export function GroupLocationLinks({
  links,
  label,
  ariaLabel,
  theme,
  target = '_self',
}: {
  /** Sibling pages — name + fully-resolved href. The current venue is excluded by the caller. */
  links: Array<{ name: string; href: string }>;
  label: string;
  ariaLabel: string;
  theme: GroupSwitcherTheme;
  /** '_blank' on embedded surfaces (iframe/host-page) so the sibling page opens a full tab. */
  target?: '_self' | '_blank';
}) {
  if (!links || links.length === 0) return null;

  // Large single-brand groups (Milos: 11 × "Estiatorio Milos <City>") would
  // render rows of near-identical text. When 3+ links share a leading-word
  // prefix, show only the distinct remainder ("Athens", "London", …) and keep
  // the full name in aria-label. The `> k + 1` bound guarantees every link
  // keeps at least one word, so a sibling named exactly the prefix can't
  // produce an empty label.
  const words = links.map((l) => l.name.split(' '));
  const first = words[0] ?? [];
  let shared = 0;
  if (links.length >= 3) {
    while (words.every((w) => w.length > shared + 1 && w[shared] === first[shared])) shared++;
  }
  const prefix = shared > 0 ? first.slice(0, shared).join(' ') + ' ' : '';

  // Vertical padding keeps the effective tap-target pitch ≥24px when the row
  // wraps (WCAG 2.5.8) despite the 12px tertiary type. Underline color follows
  // the text (not the hairline border token) so the affordance stays visible
  // on dark and light venue themes alike.
  const linkStyle: CSSProperties = {
    display: 'inline-block',
    padding: '6px 2px',
    color: theme.textSecondary,
    fontFamily: theme.fontBody,
    fontSize: 12,
    fontWeight: 500,
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    whiteSpace: 'nowrap',
  };

  return (
    <nav aria-label={ariaLabel} style={{ margin: '14px auto 0', maxWidth: 380, textAlign: 'center' }}>
      <div
        style={{
          fontSize: 11,
          color: theme.textSecondary,
          fontFamily: theme.fontBody,
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          columnGap: 12,
        }}
      >
        {links.map((l) => (
          <a
            key={l.href}
            href={l.href}
            target={target}
            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
            aria-label={prefix ? l.name : undefined}
            style={linkStyle}
          >
            {prefix && l.name.startsWith(prefix) ? l.name.slice(prefix.length) : l.name}
          </a>
        ))}
      </div>
    </nav>
  );
}
